import { getOrderEntriesForRole } from '@mummssoftware/global-context/VFGlobalContext';

export const getPrimaryFrequencyPlan = (
  rgHash: mumms.HashedRoleGroup,
  roleGroupId: number | string,
): mumms.ProcessedCarePlan | null => {
  const roleGroup = rgHash[roleGroupId.toString()];
  if (!roleGroup?.frequencies) return null;

  const plans = Object.entries(roleGroup.frequencies)
    .filter(([key]) => key !== 'dummy')
    .map(([, plan]) => plan as mumms.ProcessedCarePlan);

  if (!plans.length) {
    // No real frequency plan — role is being added for the first time.
    // Return null so no baseline row is created in order mode.
    return null;
  }

  return plans.sort(
    (a, b) =>
      new Date(b.effectiveDate || 0).getTime() -
      new Date(a.effectiveDate || 0).getTime(),
  )[0];
};

const findFrequencyKeyForPlan = (
  roleGroup: mumms.RoleGroup,
  plan: mumms.ProcessedCarePlan,
) => {
  const match = Object.keys(roleGroup.frequencies).find(
    (key) =>
      key !== 'dummy' &&
      String(roleGroup.frequencies[key]?.id) === String(plan.id),
  );
  if (match) return match;
  return Object.keys(roleGroup.frequencies).find((key) => key !== 'dummy');
};

const sortOrderModeRows = (rowA: any, rowB: any) => {
  if (rowA.order !== rowB.order) {
    return rowA.order > rowB.order ? 1 : -1;
  }

  const rowRank = (row: any) => {
    if (row.isOrderPending) return 0;
    if (row.isOrderAddTarget) return 1;
    if (row.isOrderBaseline) return 2;
    return 1;
  };

  const rankDiff = rowRank(rowA) - rowRank(rowB);
  if (rankDiff !== 0) return rankDiff;

  if (rowA.isOrderPending && rowB.isOrderPending) {
    return (
      Number(!!rowB.isNewestOrderPending) - Number(!!rowA.isNewestOrderPending)
    );
  }

  return 0;
};

/**
 * During VF/PRN orders: stack pending entries (newest first) + baseline row per role.
 * Roles without pending entries keep a single row with isOrderAddTarget for +.
 */
export const applyOrderModeToRows = (
  rows: any[],
  rgHash: mumms.HashedRoleGroup,
  orderData: Record<string, any>,
  vfOrdersMode: boolean,
) => {
  if (!vfOrdersMode) return rows;

  const rolesWithPending = new Set<string>();
  Object.values(orderData || {}).forEach((entry: any) => {
    if (entry?.id != null) rolesWithPending.add(String(entry.id));
  });

  const baseRows = rows
    .filter((row) => !rolesWithPending.has(String(row.roleGroupId)))
    .map((row) => ({
      ...row,
      isOrderAddTarget: true,
    }));

  const expandedRows: any[] = [];

  rolesWithPending.forEach((rgKey) => {
    const roleGroup = rgHash[rgKey];
    const entries = getOrderEntriesForRole(orderData, rgKey);
    const templateRow = rows.find((row) => String(row.roleGroupId) === rgKey);
    const basePlan = getPrimaryFrequencyPlan(rgHash, rgKey);

    entries.forEach((entry: any, index) => {
      expandedRows.push({
        ...(templateRow || {}),
        id: `pending-${entry.entryId}`,
        orderEntryId: entry.entryId,
        isOrderPending: true,
        isNewestOrderPending: index === 0,
        roleGroupId: Number(rgKey),
        name: entry.name || roleGroup?.name,
        order: roleGroup?.order ?? templateRow?.order,
        rowHeight: templateRow?.rowHeight || 26,
        stagedPeriod: entry.period,
        stagedFrequency: entry.frequency,
        stagedEffectiveDate: entry.effectiveDate,
        stagedDaysOfWeek: entry.daysOfWeek,
        stagedPrn: entry.prn,
        stagedDisplayKey: [
          entry.period,
          entry.frequency,
          entry.effectiveDate,
          entry.daysOfWeek,
          entry.prn,
        ].join('|'),
      });
    });

    if (roleGroup && basePlan) {
      const freqKey = findFrequencyKeyForPlan(roleGroup, basePlan);
      expandedRows.push({
        ...(templateRow || {}),
        id: freqKey,
        isOrderBaseline: true,
        roleGroupId: Number(rgKey),
        name: roleGroup.name,
        order: roleGroup.order ?? templateRow?.order,
        rowHeight: templateRow?.rowHeight || 26,
      });
    }
  });

  return [...baseRows, ...expandedRows].sort(sortOrderModeRows);
};
